/**
 * 📧 Email Obfuscation Library
 * Alles an einem Ort: Utils, Hook, Component und Konfiguration
 */

'use client';

import React, { useEffect, useState, useCallback, type ReactNode } from 'react';

// ============================================================================
// 🔧 CORE UTILITIES
// ============================================================================

const rot13 = (str: string): string => {
  return str.replace(/[a-zA-Z]/g, (char) => {
    const code = char.charCodeAt(0);
    const base = code >= 97 ? 97 : 65;
    return String.fromCharCode(((code - base + 13) % 26) + base);
  });
};

const xorEncode = (str: string, key: number): string => {
  return Array.from(str)
    .map((char) => String.fromCharCode(char.charCodeAt(0) ^ key))
    .join('');
};

export const obfuscateEmail = (email: string): string => {
  const rot13ed = rot13(email);
  const base64 = typeof window !== 'undefined' 
    ? btoa(rot13ed)
    : Buffer.from(rot13ed).toString('base64');
  
  return base64
    .replace(/=/g, '_EQ_')
    .replace(/\+/g, '_PLUS_')
    .replace(/\//g, '_SLASH_');
};

export const deobfuscateEmail = (obfuscated: string): string => {
  try {
    const base64 = obfuscated
      .replace(/_EQ_/g, '=')
      .replace(/_PLUS_/g, '+')
      .replace(/_SLASH_/g, '/');
    
    const rot13ed = typeof window !== 'undefined'
      ? atob(base64)
      : Buffer.from(base64, 'base64').toString();
    
    return rot13(rot13ed);
  } catch (error) {
    console.error('Email deobfuscation failed:', error);
    return '';
  }
};

export const obfuscateEmailAdvanced = (email: string): string => {
  const xorKey = 42;
  const xored = xorEncode(email, xorKey);
  const rot13ed = rot13(xored);
  const base64 = typeof window !== 'undefined'
    ? btoa(rot13ed)
    : Buffer.from(rot13ed).toString('base64');
  
  return base64.split('').reverse().join('');
};

export const deobfuscateEmailAdvanced = (obfuscated: string): string => {
  try {
    const xorKey = 42;
    const base64 = obfuscated.split('').reverse().join('');
    const rot13ed = typeof window !== 'undefined'
      ? atob(base64)
      : Buffer.from(base64, 'base64').toString();
    const xored = rot13(rot13ed);
    return xorEncode(xored, xorKey);
  } catch (error) {
    console.error('Advanced email deobfuscation failed:', error);
    return '';
  }
};

export const createObfuscatedMailto = (email: string, subject?: string): string => {
  if (!email) return '#';
  const subjectParam = subject ? `?subject=${encodeURIComponent(subject)}` : '';
  return `mailto:${email}${subjectParam}`;
};

// ============================================================================
// 📧 EMAIL KONFIGURATION
// ============================================================================

export const OBFUSCATED_EMAIL_DEV = '=IUQEwkQdJkUUJFVQ51dcJUQ';
export const OBFUSCATED_EMAIL_DEV_SIMPLE = 'cXJpQGdvbmJzbmJyanJmLnFy';

// ============================================================================
// 🪝 REACT HOOK
// ============================================================================

export interface UseObfuscatedEmailOptions {
  advanced?: boolean;
  revealDelay?: number;
  subject?: string;
  displayText?: string;
}

export interface UseObfuscatedEmailReturn {
  email: string;
  mailtoLink: string;
  isLoading: boolean;
  displayText: string;
  reveal: () => void;
  isRevealed: boolean;
}

export const useObfuscatedEmail = (
  obfuscatedEmail: string,
  options: UseObfuscatedEmailOptions = {}
): UseObfuscatedEmailReturn => {
  const {
    advanced = false,
    revealDelay = 0,
    subject,
    displayText: customDisplayText,
  } = options;
  
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isRevealed, setIsRevealed] = useState(revealDelay === 0);
  
  const reveal = useCallback(() => {
    if (isRevealed) return;
    
    setIsLoading(true);
    
    setTimeout(() => {
      try {
        const decoded = advanced
          ? deobfuscateEmailAdvanced(obfuscatedEmail)
          : deobfuscateEmail(obfuscatedEmail);
        
        setEmail(decoded);
        setIsRevealed(true);
      } catch (error) {
        console.error('Failed to reveal email:', error);
        setEmail('');
      } finally {
        setIsLoading(false);
      }
    }, 100);
  }, [obfuscatedEmail, advanced, isRevealed]);
  
  useEffect(() => {
    if (revealDelay === 0) {
      reveal();
    } else {
      const timer = setTimeout(reveal, revealDelay);
      return () => clearTimeout(timer);
    }
  }, [reveal, revealDelay]);
  
  const mailtoLink = email ? createObfuscatedMailto(email, subject) : '#';
  const displayText = customDisplayText || email || 'E-Mail laden...';
  
  return {
    email,
    mailtoLink,
    isLoading,
    displayText,
    reveal,
    isRevealed,
  };
};

// ============================================================================
// ⚛️ REACT COMPONENT
// ============================================================================

export interface ObfuscatedEmailLinkProps extends UseObfuscatedEmailOptions {
  obfuscatedEmail: string;
  children?: ReactNode;
  className?: string;
  title?: string;
  onClick?: (e: React.MouseEvent<HTMLAnchorElement>) => void;
}

export const ObfuscatedEmailLink: React.FC<ObfuscatedEmailLinkProps> = ({
  obfuscatedEmail,
  children,
  className,
  title,
  onClick,
  advanced = false,
  revealDelay = 0,
  subject,
  displayText: customDisplayText,
}) => {
  const { mailtoLink, displayText, isLoading } = useObfuscatedEmail(obfuscatedEmail, {
    advanced,
    revealDelay,
    subject,
    displayText: customDisplayText,
  });
  
  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (!mailtoLink || mailtoLink === '#') {
      e.preventDefault();
      return;
    }
    onClick?.(e);
  };
  
  return (
    <a
      href={mailtoLink}
      className={className}
      title={title}
      onClick={handleClick}
      rel="noopener noreferrer"
    >
      {children || displayText}
      {isLoading && <span className="sr-only">Loading...</span>}
    </a>
  );
};

// ============================================================================
// 📝 VERWENDUNG
// ============================================================================

/**
 * BEISPIEL 1: Einfacher Email-Link
 * 
 * import { ObfuscatedEmailLink, OBFUSCATED_EMAIL_DEV } from '@/app/lib/emailObfuscation';
 * 
 * <ObfuscatedEmailLink
 *   obfuscatedEmail={OBFUSCATED_EMAIL_DEV}
 *   advanced
 *   subject="Projektanfrage"
 *   className="email-link"
 * >
 *   Kontakt
 * </ObfuscatedEmailLink>
 */

/**
 * BEISPIEL 2: Hook verwenden
 * 
 * import { useObfuscatedEmail, OBFUSCATED_EMAIL_DEV } from '@/app/lib/emailObfuscation';
 * 
 * const { email, mailtoLink } = useObfuscatedEmail(OBFUSCATED_EMAIL_DEV, { advanced: true });
 */

/**
 * BEISPIEL 3: Neue Email generieren (nur Node.js!)
 * 
 * node scripts/generateObfuscatedEmail.js deine@email.de --advanced
 */
